import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react'
import secureLocalStorage from 'react-secure-storage'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'
import { TAuthData } from '../types/localStorageTypes'
import { getLocalStorage } from '../libs/helper'

interface AuthContextType {
  authData: TAuthData | null
  isAuthenticated: boolean
  login: (data: TAuthData) => void
  logout: () => void
  updateAuthData: (data: Partial<TAuthData>) => void
  refreshSession: () => Promise<boolean>
  loading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authData, setAuthData] = useState<TAuthData | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  // Counts consecutive duplicate-login mismatches so a single transient/stale
  // check-token read never kicks the active user (see the refresh interval).
  const duplicateMismatchRef = useRef(0)

  // Load auth data from storage on mount
  useEffect(() => {
    const loadAuthData = () => {
      try {
        const storedData = secureLocalStorage.getItem('authData')
        if (storedData) {
          const parsed: TAuthData = JSON.parse(storedData as string)
          setAuthData(parsed)
        }
      } catch (error) {
        console.error('Failed to load auth data:', error)
        secureLocalStorage.removeItem('authData')
      } finally {
        setLoading(false)
      }
    }

    loadAuthData()
  }, [])

  // Token refresh interval
  useEffect(() => {
    if (!authData) return

    const interval = setInterval(async () => {
      try {
        const authURL = import.meta.env.VITE_API_AUTH_URL

        if (!authData.accessToken || !authData.phoneToken) {
          console.error('Invalid auth data structure')
          return
        }

        const cookies = Object.fromEntries(
          document.cookie.split('; ').map((c) => c.split('=')),
        )
        const backup = cookies['backup_token']

        const response = await axios.get(`${authURL}/api/auth/check-token`, {
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
          },
        })

        const respData = response.data?.data
        if (!respData) {
          console.error('Invalid response data')
          return
        }

        const respToken = respData.phoneToken

        // Duplicate-login detection (single device per account). Only count it
        // as a duplicate when the server returns a concrete, non-empty
        // phoneToken that differs from ours — empty/undefined values from a
        // transient or malformed response are ignored so the active user is
        // never kicked by mistake. Require two consecutive mismatches before
        // logging out, which tolerates a brief stale read (e.g. DB replica lag
        // right after a legitimate login rotates the token) while still
        // kicking a real second login within ~40s.
        const isRealMismatch =
          typeof respToken === 'string' &&
          respToken !== '' &&
          respToken !== authData.phoneToken &&
          respToken !== backup

        if (isRealMismatch) {
          duplicateMismatchRef.current += 1
          if (duplicateMismatchRef.current >= 2) {
            duplicateMismatchRef.current = 0
            logout()
          }
          // First mismatch: wait for the next cycle to confirm before acting.
          return
        }

        // Token matches — clear any pending mismatch.
        duplicateMismatchRef.current = 0

        // Update auth data with latest info
        const updatedAuthData: TAuthData = {
          ...authData,
          expTime: respData.expTime || authData.expTime,
          status: respData.status ?? authData.status,
          vipStatus: respData.vipStatus ?? authData.vipStatus,
        }

        setAuthData(updatedAuthData)
        secureLocalStorage.setItem('authData', JSON.stringify(updatedAuthData))
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 401) {
            logout()
          } else if (!error.response) {
            console.error('Network error during token refresh')
          }
        } else {
          console.error('Unexpected error during token refresh:', error)
        }
      }
    }, 20000) // Check every 20 seconds

    return () => clearInterval(interval)
  }, [authData])

  const login = useCallback((data: TAuthData) => {
    setAuthData(data)
    secureLocalStorage.setItem('authData', JSON.stringify(data))
  }, [])

  const logout = useCallback(() => {
    setAuthData(null)
    secureLocalStorage.clear()
    navigate('/login')
  }, [navigate])

  // Silently re-authenticate with the stored credentials to obtain a brand
  // new access token. Only /api/auth/login returns the updated expTime after
  // a code is used (check-token keeps returning the old value), so this is
  // what a manual logout/login does — done automatically.
  const refreshSession = useCallback(async (): Promise<boolean> => {
    try {
      const cred = getLocalStorage('loginCred') as {
        tel: string
        password: string
      } | null

      if (!cred?.tel || !cred?.password) return false

      const authURL = import.meta.env.VITE_API_AUTH_URL
      const phoneToken = uuidv4()

      const response = await axios.post(
        `${authURL}/api/auth/login`,
        { tel: cred.tel, password: cred.password, phoneToken },
        { withCredentials: true },
      )

      const freshAuthData: TAuthData = {
        ...response.data.data,
        phoneToken,
      }

      document.cookie = `backup_token=${phoneToken}; path=/; max-age=${
        60 * 60 * 24 * 7
      }`

      setAuthData(freshAuthData)
      secureLocalStorage.setItem('authData', JSON.stringify(freshAuthData))
      return true
    } catch (error) {
      console.error('refreshSession failed:', error)
      return false
    }
  }, [])

  const updateAuthData = useCallback((updates: Partial<TAuthData>) => {
    setAuthData((prev) => {
      if (!prev) return null
      const updated = { ...prev, ...updates }
      secureLocalStorage.setItem('authData', JSON.stringify(updated))
      return updated
    })
  }, [])

  const value: AuthContextType = {
    authData,
    isAuthenticated: !!authData,
    login,
    logout,
    updateAuthData,
    refreshSession,
    loading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
